import type { AppRole } from "@/contexts/AuthContext";

export interface HelpArticle {
  id: string;
  title: string;
  summary: string;
  content: string; // markdown-style plain text
}

export interface HelpSection {
  id: string;
  emoji: string;
  title: string;
  /** Roles that can see this section. Empty = all roles. */
  visibleTo: AppRole[];
  articles: HelpArticle[];
}

/** Which roles can see a section, empty array means everyone */
const ALL: AppRole[] = [];
const EDITORS_UP: AppRole[] = ["editor", "admin", "super_admin"];
const ADMINS_UP: AppRole[] = ["admin", "super_admin"];
const SUPER_ONLY: AppRole[] = ["super_admin"];

export const helpSections: HelpSection[] = [
  // ── Getting Started ──
  {
    id: "getting-started",
    emoji: "📌",
    title: "Getting Started",
    visibleTo: ALL,
    articles: [
      {
        id: "gs-what-is",
        title: "What is Nursly and what does it do?",
        summary: "An overview of the Chadwell Heath Nursery & Pre-School platform.",
        content:
          "Nursly is the staff platform for Chadwell Heath Nursery & Pre-School. It helps the team manage children's records, attendance, staffing rotas, compliance, and more, all in one place.\n\nEverything you need to do your job is here. Your manager has given you an access level that controls what you can see and do.",
      },
      {
        id: "gs-login",
        title: "How to log in",
        summary: "Steps to log into Nursly.",
        content:
           "1. Open Nursly in your browser.\n2. Enter your email address and password.\n3. Click **Log In**.\n\nIf you've forgotten your password, click **Forgot password?** on the login page. You'll be directed to contact your manager or Owner, who can reset your password directly from the platform.",
      },
      {
        id: "gs-profile",
        title: "How to update your profile",
        summary: "Change your name, photo, or contact details.",
        content:
          "1. Click on your name or photo in the top-right corner of any page.\n2. Select **My Profile** from the menu.\n3. Update any of your details, name, job title, or profile photo.\n4. Click **Save** when you're done.\n\nYour profile photo helps colleagues recognise you around the platform.",
      },
      {
        id: "gs-password",
        title: "How to change your password",
        summary: "Update your login password.",
        content:
          "1. Go to **My Profile**.\n2. Scroll to the **Change Password** section.\n3. Enter your current password, then your new password twice.\n4. Click **Update Password**.\n\n**Password rules:** at least 8 characters, with one uppercase letter, one number, and one special character (for example ! ? £ #).",
      },
      {
        id: "gs-sidebar",
        title: "Understanding the sidebar",
        summary: "What each page in the sidebar does.",
        content:
          "The sidebar is your main menu, it runs down the left side of the screen. Each item takes you to a different section of the platform.\n\nThe sections you can see depend on your access level. If you can't see a page, it means your role doesn't need it.\n\nYou can collapse the sidebar by clicking the menu icon at the top of the page.",
      },
      {
        id: "gs-replay-tour",
        title: "How to take the platform tour",
        summary: "Walk through the guided tour anytime.",
        content:
          "1. Look for the **❓ Help** button at the bottom of the sidebar.\n2. Click it and choose **Take the platform tour**.\n3. Pick the section you want to start with, you don't have to start from the beginning.",
      },
    ],
  },

  // ── Alerts ──
  {
    id: "alerts",
    emoji: "🔔",
    title: "Alerts & Notifications",
    visibleTo: ALL,
    articles: [
      {
        id: "al-what",
        title: "What are alerts?",
        summary: "How the platform keeps you informed.",
        content:
          "Alerts are messages from the platform telling you something needs your attention. They appear on the Alerts page and as a red dot on the bell icon.\n\nThere are three types:\n- **Red (Urgent)**: something needs immediate action\n- **Amber (Warning)**: something is coming up soon\n- **Green (Info)**: a helpful update or confirmation",
      },
      {
        id: "al-find",
        title: "Where to find your alerts",
        summary: "Navigate to the Alerts page.",
        content: "Click **Alerts** in the sidebar. All your current notifications appear here, with the most recent at the top.",
      },
      {
        id: "al-read",
        title: "How to mark an alert as read",
        summary: "Dismiss alerts you've dealt with.",
        content: "Click on any alert to open it. Once viewed, it is automatically marked as read. The red dot on the bell icon disappears when all alerts have been read.",
      },
    ],
  },

  // ── Getting Help ──
  {
    id: "getting-help",
    emoji: "❓",
    title: "Getting Help",
    visibleTo: ALL,
    articles: [
      {
        id: "gh-tour",
        title: "How to take the platform tour",
        summary: "Walk through any part of the platform tour.",
        content: "Click the **❓ Help** button at the bottom of the sidebar, then choose **Take the platform tour**. You can pick a specific section to start with.",
      },
      {
        id: "gh-manager",
        title: "How to contact your manager",
        summary: "Who to speak to if you have a question.",
        content: "If you have a question that isn't covered in this manual, speak to your room leader or nursery manager. They can help with anything platform-related.",
      },
      {
        id: "gh-login-issue",
        title: "What to do if you can't log in",
        summary: "Troubleshoot login problems.",
        content:
           "1. Check you're using the correct email address.\n2. Try using the temporary password your manager gave you.\n3. If you still can't log in, ask your manager to check your account hasn't been deactivated.\n4. A Owner can reset your password directly from the Access Management page and give you a new temporary password.",
      },
      {
        id: "gh-ai-search",
        title: "Using the AI Help Search",
        summary: "How the in-app AI assistant works and its limits.",
        content:
          "The Help page includes an AI search box that answers questions about Nursly using only this manual. It will not invent features or guess pricing.\n\n- **Rate limit:** 20 questions per hour per user.\n- **Question length:** up to 500 characters per question.\n- **Privacy:** your question is sanitised before being sent to the AI provider, children's names, parent details, and other personal information are not included.\n\nIf the AI is not 100% confident, it will tell you to email support@nursly.co.uk.",
      },
      {
        id: "gh-support",
        title: "How to contact Nursly support",
        summary: "Email support for help at any time.",
        content: "If you need help at any time, email support@nursly.co.uk. We respond within one working day. The support email is also visible in the footer at the bottom of every platform page.",
      },
    ],
  },

  // ── Platform Access & Your Staff Profile ──
  {
    id: "platform-access",
    emoji: "🔗",
    title: "Platform Access & Your Staff Profile",
    visibleTo: ALL,
    articles: [
      {
        id: "pa-what-linked",
        title: "What does 'Platform access linked' mean on my profile?",
        summary: "Understanding the link between your login and staff profile.",
        content:
          "When your platform login email matches the email on your staff profile, the two are **linked**. This link is what gives you access to the Room Register during your shifts.\n\nYou can see your link status on your staff profile, look for a green **✅ Platform access linked** badge.",
      },
      {
        id: "pa-email-matters",
        title: "Why does my email matter for Room Register access?",
        summary: "Your email connects your login to your shifts.",
        content:
          "Your login email is the key that connects your platform account to your staff profile. If these emails don't match, the platform can't verify your zone and shift assignments, so it can't give you Room Register access.\n\nIf your email has changed, tell your manager so they can update your staff profile to match.",
      },
      {
        id: "pa-not-linked",
        title: "What do I do if my Room Register shows 'account not linked to a staff profile'?",
        summary: "Steps to resolve a missing link.",
        content:
          "This means your platform login email doesn't match any email on the staff profiles. Speak to your manager, they can either:\n- Update the email on your staff profile to match your login email, or\n- Manually link your account from the Access Management page.\n\nOnce linked, your Room Register access will work automatically based on your rota shifts.",
      },
      {
        id: "pa-change-email",
        title: "Can I change my login email?",
        summary: "What happens if you need a different email.",
        content:
          "Changing your login email affects your Room Register access because the link between your account and staff profile depends on matching emails. Speak to your manager, they'll need to update your staff profile email to match your new login email.",
      },
      {
        id: "pa-admin-check-links",
        title: "How to check which staff have linked platform accounts",
        summary: "Admins: view link status on the Staff page.",
        content:
          "Go to the **Staff** page. Look at the **Platform Access** column, it shows the link status for each staff member:\n- **✅ Linked**: account and staff profile are connected\n- **🕐 Invite pending**: invited but haven't completed setup yet\n- **⚠️ No access**: no platform account linked\n- **❌ Deactivated**: account has been deactivated\n\nClick **⚠️ No access** to send a platform invite directly.",
      },
      {
        id: "pa-send-invite-from-staff",
        title: "How to send a platform invite from a staff profile",
        summary: "Admins: invite directly from the Staff page.",
        content:
          "On the **Staff** page, find the staff member with an **⚠️ No access** indicator in the Platform Access column. Click the indicator, a dialog will appear with their name and email pre-filled. Choose their access level and click **Send Invite**.",
      },
      {
        id: "pa-manual-link",
        title: "How to manually link a platform account to a staff profile",
        summary: "Admins: link when emails don't match.",
        content:
          "If a staff member uses a personal email for their login but a work email on their staff profile, you can manually link them:\n\n1. Go to **Access Management**.\n2. Find the user in the list.\n3. Click the **Link Profile** button.\n4. Search for and select the correct staff profile.\n5. Confirm the link.\n\nOnce linked, their Room Register access works automatically.",
      },
      {
        id: "pa-bulk-audit",
        title: "How to run the bulk link audit",
        summary: "Admins: find all unlinked accounts at once.",
        content:
          "On the **Access Management** page, click **Review Unlinked**. This shows:\n- Platform accounts with no linked staff profile\n- Staff profiles with no linked platform account\n- Suggested matches based on name similarity\n\nYou can confirm suggested matches with one click.",
      },
      {
        id: "pa-email-update-sync",
        title: "Does the link update automatically when an email changes?",
        summary: "Admins: understanding real-time email sync.",
        content:
          "Yes. The link between platform accounts and staff profiles is based on email matching. If you update the email on a staff profile, the link updates automatically in real time. No manual re-linking is needed.",
      },
      {
        id: "pa-deactivate",
        title: "How to deactivate a staff member's platform access when they leave",
        summary: "Admins: remove access for leavers.",
        content:
          "Go to **Access Management**, find the user, and deactivate their account. Their staff profile remains for historical records (attendance, shifts, etc.), but they can no longer log in or access the platform.",
      },
      {
        id: "pa-permissions-guide",
        title: "The Access and Permissions guide",
        summary: "Shows exactly what each access level can see and do.",
        content: "Go to **Access Management** in the sidebar and click **Permissions guide** at the top of the page.\n\nThe guide lists the five access levels, Owner, Admin, Front Desk, Editor and Viewer, and shows, page by page, what each one can view, add, edit or approve.\n\nUse it before you invite someone: pick the lowest level that still lets them do their job. If you are unsure, start with Viewer or Editor. You can change someone's level later from Access Management without removing their account.\n\nWhy this matters: it keeps sensitive parent and child information limited to the people who genuinely need it, which is exactly what an inspector will expect.",
      },
      {
        id: "pa-invite-with-guide",
        title: "How to choose the right access level when inviting staff",
        summary: "A quick rule of thumb for each role.",
        content: "- **Viewer**: room staff. Can sign children in and out, and tick club pickups and drop-offs, for their own room. Cannot edit anything, and cannot see or act on any other room. Good for practitioners, students and bank staff.\n- **Editor**: senior room staff. Everything a Viewer can do, plus editing child profiles and session details, logging medicine given, and editing the rota, all for their own room.\n- **Admin**: deputy or nursery manager. Full day-to-day access across every room: children, staff, rota, approvals, compliance and reports. No billing.\n- **Owner**: everything an Admin can do, plus billing and account control. Owner was previously called Super Admin.\n\nSomeone's job title on their staff profile, for example Nursery Manager, does not decide what they can do here. Only the access level does.\n\nWhen you send an invite from **Access Management** or from a staff profile, you pick the level on the invite form. Read the **Permissions guide** first if you are not sure.",
      },
    ],
  },

  // ── Kiosk ──
  {
    id: "kiosk",
    emoji: "📲",
    title: "Kiosk (parent sign-in screen)",
    visibleTo: ALL,
    articles: [
      {
        id: "kiosk-what",
        title: "What is Kiosk mode?",
        summary: "The tablet screen parents use to sign their child in and out.",
        content:
          "Kiosk mode is the full-screen tablet view parents use at the door to sign their child in and out. Parents search for their child's first or last name, tap the name, and say who is dropping off or collecting.\n\nKiosk only shows children who are scheduled to attend that day, so the list stays short and accurate, and every sign-in counts toward the correct day's ratios.",
      },
      {
        id: "kiosk-child-missing",
        title: "Why can't I find a child in Kiosk or Register?",
        summary: "Child not showing, can't find child, kiosk missing name, new child not appearing.",
        content:
          "Kiosk and Register only show children scheduled to attend that day. Check the child's profile has session days set. If none are set, you will see an Incomplete badge until this is fixed.\n\nCommon reasons a child is not showing, a name cannot be found, a kiosk name is missing, or a new child is not appearing:\n- **No session days set.** This is the most common cause for a newly added child. Open the child's profile, go to the Nursery step, choose their days, and save. They appear straight away.\n- **Not booked in today.** If today is not one of their session days, they will not appear. For a one-off extra day, use an Additional Day request instead, which keeps ratios correct.\n- **Start date is in the future.** The child is Pending and will appear from their start date.\n- **The child has been archived**, or their end date has passed.\n\nAdmins and Owners see a reminder banner on the Dashboard and the Children page listing every child with no session days set, with a **Review children** button that filters the Children list to just those profiles.\n\nViewers and Editors cannot edit child profiles, so if a Kiosk search finds no match, ask an Admin to check the child's session days are set.",
      },
      {
        id: "kiosk-days-badge",
        title: "What does the Incomplete badge 'No session days set' mean?",
        summary: "Why a child shows an amber Incomplete badge.",
        content:
          "It means the child has started (their start date is today or earlier) but no session days have been chosen yet, so they cannot appear in Kiosk or on the Register.\n\nYou can still save a child without session days, useful during a migration or a provisional registration, but you will be asked to confirm, and the amber Incomplete badge stays visible until the days are added.",
      },
      {
        id: "kiosk-not-scheduled",
        title: "Can I sign a child in on a day they are not booked for?",
        summary: "No, use an Additional Day request instead.",
        content:
          "No. Signing a child in on an unscheduled day would bypass the ratio check for that day and would be impossible to tell apart from a normal booked day in the Register, attendance exports and the Ofsted pack.\n\nIf a parent needs an extra day, raise an **Additional Day** request. Once approved, the child appears in Kiosk and Register for that date only, and ratios are checked properly.",
      },
    ],
  },

  // ── Room Register ──
  {
    id: "room-register",
    emoji: "🏠",
    title: "Room Register",
    visibleTo: ALL,
    articles: [
      {
        id: "rr-what",
        title: "What is the Room Register?",
        summary: "The daily sign-in / sign-out sheet for your room.",
        content:
          "The Room Register is where you record children arriving and leaving your room each day. It works like a digital version of the paper sign-in sheet.\n\nYou'll only see children assigned to your zone, so the list is always relevant to your room.\n\nTo access the Room Register, click **Room Register** in the sidebar under the Staff section.",
      },
      {
        id: "rr-access",
        title: "Why can't I access the Room Register?",
        summary: "Four things needed for Room Register access.",
        content:
          "There are four things needed to access the Room Register:\n1. Your platform login email must match the email on your staff profile, if these don't match, speak to your manager\n2. Your staff profile must have a zone assigned (Baby Zone, Toddler Zone, or Pre-School)\n3. You must have a shift scheduled on the rota for today in that zone\n4. The current time must be within your shift hours\n\nIf any of these are missing, the Room Register will show a message explaining exactly what needs to be resolved. Speak to your manager if you're unsure.",
      },
      {
        id: "rr-shift-started",
        title: "My shift has started but I still can't access the Room Register",
        summary: "Usually means your account is not linked.",
        content:
          "This usually means your platform account is not yet linked to your staff profile. Your manager needs to link your account, they can do this from the Staff page or the Access Management page. Once linked, your Room Register access will work automatically based on your rota shifts.",
      },
      {
        id: "rr-diff",
        title: "How is it different from the Main Register?",
        summary: "Room Register vs Main Register explained.",
        content:
          "The **Room Register** is specific to your zone (e.g. Baby Zone). The **Main Register** covers all children across the whole nursery.\n\nAs a room-based staff member, you'll mostly use the Room Register. Managers use the Main Register for the full-nursery view.",
      },
      {
        id: "rr-signin",
        title: "How to sign a child in",
        summary: "Record a child's arrival.",
        content: "1. Open the **Room Register** from the sidebar under the Staff section.\n2. Find the child's name in the list.\n3. Click the **Sign In** button on their row.\n4. The time is recorded automatically.\n\n**Who can do this:** everyone working in the room, including **Viewers**. Viewers and Editors see and act on their own assigned room only. Admins and Owners can see every room.",
      },
      {
        id: "rr-signout",
        title: "How to sign a child out",
        summary: "Record a child's departure.",
        content: "1. Find the child on the Room Register.\n2. Click the **Sign Out** button.\n3. The departure time is recorded.\n\nTry to do this in real time so the records are accurate.\n\n**Who can do this:** everyone working in the room, including **Viewers**, for their own assigned room.",
      },
      {
        id: "rr-manual",
        title: "How to enter a time manually",
        summary: "What to do if you forgot to sign a child in or out.",
        content: "If you missed the real-time sign-in or sign-out, you can click **Enter time manually** and type the correct time. Use this only when necessary, real-time recording is always preferred.",
      },
      {
        id: "rr-absent",
        title: "What does 'Absent' mean on the Room Register?",
        summary: "Understanding absence on the register.",
        content: "If a child is marked as **Absent**, it means they are not expected to attend today, or their absence has been recorded by a manager with a reason (e.g. illness, holiday).",
      },
      {
        id: "rr-admin-zones",
        title: "How to view all zones at once (Admins)",
        summary: "Admins: use the All Zones tab on the Room Register page.",
        content: "On the Room Register page, use the zone tabs at the top to switch between zones. Select **All Zones** to see a consolidated view of all zones at once. Each zone appears as a collapsible section with its colour header.",
      },
    ],
  },

  // ── Main Register ──
  {
    id: "main-register",
    emoji: "📋",
    title: "Main Register",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "mr-what",
        title: "What is the Main Register?",
        summary: "The nursery-wide attendance register.",
        content: "The Main Register shows every child across all zones for the current day. It's the primary record of who is in the building.",
      },
      {
        id: "mr-present",
        title: "How to mark a child as present",
        summary: "Record attendance on the main register.",
        content: "Find the child's row and click **Sign In**. The time is recorded automatically.",
      },
      {
        id: "mr-absent",
        title: "How to mark a child as absent",
        summary: "Record an absence with a reason.",
        content: "1. Find the child on the register.\n2. Click the **Absent** button.\n3. Select a reason from the dropdown (e.g. Illness, Holiday, Family Emergency).\n4. Add any notes if needed.\n5. Confirm.",
      },
      {
        id: "mr-undo",
        title: "What the 5-minute undo button does",
        summary: "Reverse a mistake quickly.",
        content: "After signing a child in or out, a small **Undo** button appears for 5 minutes. If you tapped the wrong child or recorded the wrong action, click Undo immediately to reverse it.",
      },
      {
        id: "mr-correction",
        title: "How to correct a wrong entry",
        summary: "Fix a mistake on the register.",
        content: "If more than 5 minutes have passed, you'll need to submit a correction request. Click the **⋯** menu on the child's row and select **Correct Entry**. Your manager will review and approve the change.",
      },
    ],
  },

  // ── Children ──
  {
    id: "children",
    emoji: "👶",
    title: "Children",
    // Front Desk works nursery-wide with new starters and child details.
    visibleTo: [...EDITORS_UP, "front_desk"],
    articles: [
      {
        id: "ch-find",
        title: "How to find a child's profile",
        summary: "Search for any child on the platform.",
        content: "1. Click **Children** in the sidebar.\n2. Use the search bar at the top to type the child's name.\n3. Click on their name to open their full profile.",
      },
      {
        id: "ch-info",
        title: "What information is on a child's profile",
        summary: "Understanding the child profile page.",
        content: "A child's profile includes their personal details, zone assignment, session times, funding status, parent/carer contacts, emergency contacts, and any flags or notes.",
      },
      {
        id: "ch-zones",
        title: "Understanding zone badges",
        summary: "What the coloured badges mean.",
        content: "Each child has a zone badge showing which room they're assigned to:\n- **Baby Zone**: youngest children\n- **Toddler Zone**: middle age group\n- **Pre-School**: oldest children, preparing for school",
      },
      {
        id: "ch-edit-editor",
        title: "How to suggest an edit to a child's profile",
        summary: "Editors: submit a change for approval.",
        content: "1. Open the child's profile.\n2. Click **Edit** on the section you want to change.\n3. Make your changes and click **Submit for Approval**.\n4. Your manager will receive a notification and review the change.\n5. Once approved, the change goes live. You'll see a **Pending Approval** badge until then.\n\n**Who can do this:** Editors and above. **Viewers cannot edit a child's profile or session details**: they can sign children in and out and tick club pickups for their own room only.",
      },
      {
        id: "ch-edit-admin",
        title: "How to edit a child's profile directly",
        summary: "Admins: make changes that take effect immediately.",
        content: "1. Open the child's profile.\n2. Click **Edit** on the section you want to change.\n3. Make your changes and click **Save**.\n4. The change takes effect immediately.\n\n**Who can do this:** Admins and Owners, across every room.",
      },
      {
        id: "ch-add",
        title: "How to add a new child",
        summary: "Create a new child record.",
        content: "1. Go to the **Children** page.\n2. Click **Add Child**.\n3. Complete the same form and checks used by an Admin.\n4. Click **Add Child**.\n\nFront Desk, Editors, Admins and Owners can add children. Front Desk can also review an enrolment request and click **Enrol child** once the required details are complete. Both paths follow the Starter limit of 25 active children. Front Desk can archive children who have left. Spreadsheet import and restoring archived children are Admin and Owner only.",
      },
      {
        id: "ch-minimal-info",
        title: "Starting a child profile with only the basics",
        summary: "You only need a first and last name to begin.",
        content: "You do not need every detail before you can save a child. Add the first and last name, save, and fill in the rest when the parent brings the paperwork in.\n\nWhile details are missing, the profile shows an **Incomplete profile** badge with a list of what is still needed. Add the missing items when you have them and the badge disappears.\n\nWhy this matters: you can get a new starter on the system on the day they are offered a place, instead of keeping notes on paper.",
      },
      {
        id: "ch-remove-test-data",
        title: "I added a child or entered attendance as a test, how do I remove it?",
        summary: "Test children are archived, not deleted. Register entries stay for good but harm nothing.",
        content: "**Test children: archive them.**\n\n1. Go to the **Children** page.\n2. Find the child, then click the labelled **Archive** button on their row, or open their profile and click **Archive** at the top.\n3. Choose a reason and confirm. The reason can't be left blank.\n\n**Who can archive:** Front Desk, Admins and Owners. Editors and Viewers cannot archive a child. Restoring an archived child back into the Children list is Admin and Owner only.\n\nChild profiles are archived rather than deleted, because nurseries in the UK must keep children's records for several years after they leave. An archived child is out of the way completely: they do not appear in the Children list, the Register, the rota, dashboard counts, ratios, exports or the Ofsted pack. They sit quietly in the **Archived** tab on the Children page if you ever need them again.\n\n**Test register entries: leave them.**\n\nSign-ins and sign-outs are kept permanently once created, for the same reason: an attendance record is evidence of who was in your setting and when, so it cannot be edited away. If you spot a mistake straight away, the **Undo** button next to the entry removes it within 5 minutes of you making it. After that it stays.\n\nDon't worry about test entries. They belong to children you have archived, so they do not affect your real numbers, your ratios, your funding claims or your Ofsted readiness score in any way. Nothing you did while trying the platform out counts against you.\n\n**Note:** nothing on Nursly permanently deletes a child or an attendance entry, and that is deliberate. If you need a whole account removed, an Owner can do that from **Settings**.",
      },
      {
        id: "ch-allergies-export",
        title: "Allergies and medical conditions list",
        summary: "Print or download a list of every child with an allergy or medical need.",
        content: "1. Go to the **Children** page.\n2. Click the **Export** menu at the top right.\n3. Choose **Allergies list, PDF** to print, or **Allergies list, CSV** to open in Excel.\n\nThe list shows each child's name, zone, allergy or medical condition, and their emergency contact. Children with no allergies recorded are left out.\n\nWhy this matters: the PDF is designed to be printed and pinned up in the kitchen and each room, so anyone preparing food can check at a glance. Print a fresh copy whenever a child's details change.",
      },

    ],
  },

  // ── Staff ──
  {
    id: "staff",
    emoji: "👥",
    title: "Staff",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "st-find",
        title: "Where to find staff names and job titles",
        summary: "View the staff list.",
        content: "Click **Staff** in the sidebar. You'll see a list of all team members with their name and job title.",
      },
      {
        id: "st-profile",
        title: "How to view a full staff profile",
        summary: "Admins: access full staff details.",
        content: "Click on any staff member's name to open their full profile. This includes contact details, qualifications, DBS status, and First Aid expiry dates.",
      },
      {
        id: "st-dbs",
        title: "Understanding DBS and First Aid badges",
        summary: "What the coloured badges mean on the staff list.",
        content: "Staff profiles show badge indicators for DBS and First Aid certificates:\n- **Grey**: valid (more than 60 days remaining)\n- **Amber**: expiring within 31-60 days\n- **Red**: expiring within 30 days or already expired\n- **Italic grey text**: not recorded",
      },
      {
        id: "st-add",
        title: "How to add a new staff member",
        summary: "Create a new staff record.",
        content: "1. Go to the **Staff** page.\n2. Click **Add Staff**.\n3. Fill in their name, job title, qualifications, and zone assignment.\n4. Click **Save**.",
      },
      {
        id: "st-ops",
        title: "What Operations staff are",
        summary: "Non-childcare team members explained.",
        content: "Operations staff are team members who don't work directly in a childcare zone, like receptionists, cleaners, and caretakers. They appear on the Staff page and Rota but aren't assigned to a specific zone.",
      },
      {
        id: "st-platform-access",
        title: "Understanding the Platform Access column",
        summary: "What each status means on the staff list.",
        content: "The **Platform Access** column on the Staff page shows whether each staff member has a linked platform login:\n- **✅ Linked**: their login email matches their staff profile email\n- **🕐 Invite pending**: they've been invited but haven't completed setup\n- **⚠️ No access**: no platform account exists for them\n- **❌ Deactivated**: their account has been deactivated\n\nClick **⚠️ No access** to send a platform invite directly from the staff list.",
      },
      {
        id: "st-minimum-fields",
        title: "You only need a name to add a staff member",
        summary: "Start the profile now, finish it later.",
        content: "To add someone to the Staff page you only need their **first name** and **last name**. Everything else can wait.\n\n1. Go to **Staff** and click **Add Staff**.\n2. Type the first and last name.\n3. Click **Save**.\n\nThe new profile shows an **Incomplete profile** badge listing what is still missing. You can put them on the rota straight away.\n\nWhy this matters: on a busy day you can get a new starter or an agency worker on the system in seconds, rather than waiting until you have all their paperwork.",
      },
      {
        id: "st-complete-later",
        title: "What to add to a staff profile later",
        summary: "The details needed for full compliance tracking.",
        content: "Add these when you have them, so the compliance checks and Ofsted pack are complete:\n\n- **Email address**: links their platform login and their Room Register access\n- **Job title and role level**: used for ratio and Level 3 manager checks\n- **Start date**: needed for rota and hours reporting\n- **Zone or zones**: where they normally work\n- **DBS number and issue date**: tracked with expiry warnings\n- **Paediatric First Aid certificate dates**: needed for first aid cover checks\n- **Qualifications**: needed for the Level 3 requirement\n- **Emergency contact and phone number**\n\nOpen the staff profile, click **Edit**, and add anything on the **Incomplete profile** list. Missing DBS, First Aid, or qualifications will show as gaps on the Compliance page until they are filled in.",
      },
    ],
  },

  // ── Rota & Shifts ──
  {
    id: "rota",
    emoji: "📅",
    title: "Rota & Shifts",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "ro-read",
        title: "How to read the rota",
        summary: "Understanding the weekly shift view.",
        content: "The Rota page shows a weekly grid with staff members down the left side and days of the week across the top. Each cell shows the shift assigned to that person on that day.",
      },
      {
        id: "ro-types",
        title: "Understanding shift types",
        summary: "What each shift type means.",
        content: "- **Starter**: early morning shift\n- **Finisher**: late afternoon shift\n- **Mid-Day**: middle of the day shift\n- **Full Day**: covers the entire day\n- **Other Duties**: non-zone duties (e.g. training)\n- **Operations**: for non-childcare staff",
      },
      {
        id: "ro-add",
        title: "How to add a shift",
        summary: "Admins: assign a shift to a staff member.",
        content: "**Who can do this:** Editors, Admins and Owners. Editors can add, change and remove shifts for their own room only. Admins and Owners can edit the rota for every room. Viewers can see the rota but cannot change it.\n\n1. Click on an empty cell in the rota grid.\n2. Choose the shift type, start time, end time, and zone.\n3. Click **Save**.",
      },
      {
        id: "ro-templates",
        title: "How to use shift templates",
        summary: "Save time with pre-set shift patterns.",
        content: "Shift templates are pre-saved shift configurations. When adding a shift, you can click **Use Template** to apply a template instead of entering times manually.",
      },
      {
        id: "ro-payroll",
        title: "How do I download staff attendance for payroll?",
        summary: "Use Payroll report, pick any dates, get actual sign-in and sign-out times.",
        content: "The Rota page has two different downloads:\n\n- **Payroll report (choose any dates)** — for pay runs. Pick any date range, such as a full calendar month.\n- **Export this week** — a quick copy of the week you are looking at, nothing more.\n\n**For payroll, always use Payroll report:**\n\n1. On the Rota page, click **Payroll report (choose any dates)**.\n2. Set the From and To dates, or tap This Month or Last Month.\n3. Narrow it to one person or one room if you need to.\n4. Click **Export CSV**.\n\nThe file lists every day for each person with the planned start and finish, and the **actual sign-in and sign-out times** taken from the kiosk or recorded by hand, the actual hours, and the difference from the planned hours. That means you are paying on what really happened, not only what was planned.",
      },
      {
        id: "ro-export",
        title: "How to download this week's rota",
        summary: "Quick copy of the week on screen.",
        content: "1. Click **Export this week** on the Rota page.\n2. Choose Colour PDF, Black & White PDF, or CSV.\n\nThis only covers the week you are viewing. For pay runs use **Payroll report (choose any dates)** instead.",
      },
      {
        id: "ro-zone-export",
        title: "How to export by zone",
        summary: "Filter the rota export to a specific zone.",
        content: "In the export dialog, select a specific zone (e.g. Baby Zone) from the Zone dropdown. Only staff with shifts in that zone during the selected date range will be included.",
      },
      {
        id: "ro-compliance",
        title: "Understanding ratio compliance badges",
        summary: "What At Ratio, Over-Staffed, and Understaffed mean.",
        content: "At the bottom of the rota, compliance badges show whether each zone meets its legal staffing ratio:\n- **At Ratio**: correctly staffed\n- **Over-Staffed**: more staff than required\n- **Understaffed**: fewer staff than required (needs attention)",
      },
      {
        id: "ro-kiosk-actual-hours",
        title: "Actual hours from the staff kiosk",
        summary: "What the 📍 pin on a shift means.",
        content: "If a staff member signs in and out on the kiosk tablet, the real times are saved against their shift on the rota. A **📍** on the shift means the times came from the kiosk rather than being typed in by a manager.\n\nThe shift shows the planned time and, underneath, the actual time. If the two differ by more than **30 minutes** you will see a variance warning, so early arrivals and late finishes are easy to spot before payroll.",
      },
      {
        id: "ro-manual-actual-hours",
        title: "Recording actual hours without a kiosk tablet",
        summary: "How to type in real start and finish times by hand.",
        content: "You do not need a kiosk tablet to keep actual hours. Planned hours are the times you set on the rota, actual hours are the times someone really started and finished, and you can enter or correct them by hand at any time.\n\n1. Go to **Rota** and find the person and the day.\n2. On the shift, tap the round **tick** button (bottom right of the shift, always visible on a phone or tablet, appears when you hover on a computer).\n3. Enter the real start and finish time, or tick **Staff did not attend this shift**.\n4. Choose a reason if the times differ from the plan, then save.\n\nYou can use the same tick again later to correct times, including times that came from the kiosk. Anything you enter by hand shows as **Entered by hand** on Attendance & Staffing · Staff, and feeds the Payroll report.",
      },
      {
        id: "ro-auto-signout",
        title: "What 'Auto sign-out: verify hours' means",
        summary: "The staff member forgot to sign out, so the system closed the shift.",
        content: "If someone signs in on the kiosk but never signs out, Nursly closes the shift automatically overnight and marks it **Auto sign-out: verify hours**.\n\nThis is a flag, not a finished record. It means: we know when they arrived, but nobody confirmed when they left, so the hours may be wrong.\n\n**How to correct it:**\n1. Go to **Rota** and find the shift with the warning.\n2. Click the shift to open it.\n3. Check the actual finish time with the staff member.\n4. Enter the correct finish time and save.\n\nThe warning clears once a manager has confirmed the time. You will be asked for your passcode, because changing actual hours affects pay.\n\nWhy this matters: unverified hours can mean someone is paid for time they did not work, or not paid for time they did. It also makes your staffing records look inaccurate to an inspector.",
      },
    ],
  },

  // ── Attendance ──
  {
    id: "attendance",
    emoji: "📊",
    title: "Attendance",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "at-view",
        title: "How to view attendance data",
        summary: "Check attendance records.",
        content: "Click **Attendance** in the sidebar. You can view attendance data by day, week, or month. Use the date picker to navigate between periods.",
      },
      {
        id: "at-export",
        title: "How to export an attendance report",
        summary: "Download attendance data.",
        content: "Click the **Export** button on the Attendance page. Choose your date range and click **Download**. The report includes all attendance records for the selected period.",
      },
    ],
  },

  // ── Transitions ──
  {
    id: "transitions",
    emoji: "🔄",
    title: "Transitions",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "tr-what",
        title: "What is a zone transition?",
        summary: "Moving a child between rooms.",
        content: "A transition is the process of moving a child from one zone to another, for example, from Baby Zone to Toddler Zone. Transitions follow a structured process with multiple stages.",
      },
      {
        id: "tr-status",
        title: "Understanding transition statuses",
        summary: "What each status means.",
        content: "- **Suggested**: someone has recommended a transition\n- **Scheduled**: the transition has a planned date\n- **Taster**: the child is visiting the new zone\n- **Transitioning**: the child is gradually moving\n- **Complete**: the child is fully in the new zone\n- **Cancelled**: the transition was called off",
      },
      {
        id: "tr-progress",
        title: "How to progress a transition",
        summary: "Admins: move a transition to the next stage.",
        content: "1. Go to the **Transitions** page.\n2. Find the transition you want to update.\n3. Click the **Progress** button to move it to the next stage.\n4. Add any notes about how the transition is going.",
      },
      {
        id: "tr-age-automation",
        title: "Age-based transition automation",
        summary: "How transitions are proposed automatically.",
        content: "Nursly proposes a transition automatically **3 months before** a child reaches the next zone's age threshold (Baby → Toddler at 2 years, Toddler → Pre-School at 3 years, Pre-School → Starting School at 4 years where applicable).\n\nThe proposed date defaults to the child's birthday but can be modified, open the transition and adjust the **Scheduled date** to any working day. You can also cancel a proposed transition if the child is staying in their current zone (for example, awaiting space in the next room).",
      },
      {
        id: "tr-cohort-filter",
        title: "Academic year cohort filter",
        summary: "Plan whole September moves together.",
        content: "On the Transitions page, the **Academic year cohort** filter groups every child whose proposed transition falls within the same school year (1 September to 31 August). Use it to plan room moves for a whole intake in one go, open each child, agree dates with parents, then progress through Taster and Transitioning together.",
      },
    ],
  },



  // ── Planned Changes ──
  {
    id: "planned-changes",
    emoji: "📆",
    title: "Planned Changes",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "pc-what",
        title: "What is a Planned Change?",
        summary: "Scheduling future changes to a child's sessions.",
        content: "A Planned Change is a future change to a child's session times, days, or zone that is scheduled to take effect on a specific date. On that date, the change is applied automatically.",
      },
      {
        id: "pc-create",
        title: "How to create a Planned Change",
        summary: "Admins: schedule a future change.",
        content: "1. Go to the **Planned Changes** page.\n2. Click **Add Planned Change**.\n3. Select the child, the type of change, the new value, and the effective date.\n4. Click **Save**.",
      },
      {
        id: "pc-cancel",
        title: "How to cancel a Planned Change",
        summary: "Stop a scheduled change before it takes effect.",
        content: "On the Planned Changes page, find the change you want to cancel and click **Cancel**. This can only be done before the effective date.",
      },
    ],
  },

  // ── Approvals ──
  {
    id: "approvals",
    emoji: "✅",
    title: "Approvals",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "ap-what",
        title: "What is the Approvals page?",
        summary: "Where you review changes submitted by Editors.",
        content: "When an Editor makes a change, like updating a child's profile, it doesn't go live straight away. Instead, it lands on the Approvals page for you to review.",
      },
      {
        id: "ap-approve",
        title: "How to approve a change",
        summary: "Accept a submitted change.",
        content: "1. Go to the **Approvals** page.\n2. Find the pending request.\n3. Review the change summary.\n4. Click **Approve** to make the change go live.",
      },
      {
        id: "ap-reject",
        title: "How to reject a change",
        summary: "Decline a submitted change.",
        content: "1. Find the pending request on the Approvals page.\n2. Click **Reject**.\n3. Enter a reason so the Editor understands why.\n4. The Editor will be notified.",
      },
      {
        id: "ap-info",
        title: "How to request more information",
        summary: "Ask the Editor for clarification before deciding.",
        content: "If you're not sure about a change, click **Request Info** instead of approving or rejecting. Type your question, the Editor will receive a notification and can respond.",
      },
    ],
  },

  // ── Funding ──
  {
    id: "funding",
    emoji: "£",
    title: "Funding",
    visibleTo: [...ADMINS_UP, "front_desk"],
    articles: [
      {
        id: "fu-what",
        title: "What is the Funding page?",
        summary: "Managing government and private funding for children.",
        content: "The Funding page shows the funding status of each child, whether they receive government-funded hours (15 or 30 hours) or are privately paid.",
      },
      {
        id: "fu-types",
        title: "Understanding funding types",
        summary: "Government Funded, Extended, Privately Paid explained.",
        content: "- **Government Funded 15hrs**: universal entitlement\n- **Government Funded 30hrs**: extended entitlement for eligible families\n- **Extended hours**: additional hours beyond funded entitlement\n- **Privately Paid**: no government funding",
      },
      {
        id: "fu-update",
        title: "How to update a child's funding status",
        summary: "Change funding details on a child's profile.",
        content: "Front Desk, Admins and Owners can see, enter and change funding codes. Editors and Viewers cannot read or change them; saving other child details never changes a stored code.\n\n1. Go to the child's profile and click **Edit**.\n2. Find the **Funding** section.\n3. Update the funding status, code, and expiry date.\n4. Click **Save**.\n\nYou can also edit a code directly on the **Funding** page, enter it when adding a child or reviewing an enrolment request, or supply the **Funding Code** column in a child spreadsheet import. Parents are not asked for funding codes on enrolment forms.",
      },
    ],
  },

  // ── Plans & Billing ──
  {
    id: "plans-billing",
    emoji: "💳",
    title: "Plans & Billing",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "pb-tiers",
        title: "Trials and existing plans",
        summary: "What's included on each plan.",
        content:
          "New nurseries start a **14-day free trial** of full Professional through Stripe Checkout. Payment details are required with **£0 today**, then the chosen plan is charged automatically: **£49 a month** or **£490 a year. 2 months free.** Cancel in **Settings > Plan & Billing** before the trial ends to avoid a charge. The Owner receives a reminder three days before expiry. Cancelling keeps access to the end of the trial or paid period, then you can still view children and staff and download your information. Returning does not include a second trial.\n\nExisting **Starter** nurseries keep their free plan for up to **25 active children**. It includes everything you need to run a small setting, registers, rota, compliance, and the audit log.\n\n**Professional** is **£49 a month**. Annual: **£490 a year. 2 months free.** It removes the 25-child cap and unlocks Out of School, advanced reporting, the staffing scenario planner, free data migration, and priority support.\n\nBoth plans use the same encryption, audit logging, and 6-year data retention.",
      },
      {
        id: "pb-upgrade",
        title: "How to upgrade or change plan",
        summary: "Switch between Starter and Professional.",
        content:
          "1. Go to **Settings > Plan & Billing** (Owners only).\n2. Choose **Monthly** or **Annual**.\n3. Enter payment details, billing is handled by Stripe.\n\nNew nurseries start with a **14-day free trial**. Payment details are required, with £0 today. Cancel in **Settings > Plan & Billing** before it ends to avoid a charge. Existing nursery plans and prices are unchanged.",
      },
      {
        id: "pb-referrals",
        title: "Referral rewards",
        summary: "Earn 2 free months of Professional for each nursery you refer.",
        content:
          "Every nursery has a unique referral code, visible in **Settings > Referrals**. When a nursery you refer signs up and starts a Professional plan, **2 months of Professional are automatically added to your account** as a Stripe credit. Rewards apply at the nursery level, not the individual user.",
      },
    ],
  },

  {
    id: "compliance",
    emoji: "🛡",
    title: "Compliance",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "co-what",
        title: "What is the Compliance page?",
        summary: "Track staff qualifications and certificates.",
        content: "The Compliance page gives you an overview of all staff qualifications, DBS checks, First Aid certificates, and Paediatric First Aid status. It highlights anything that needs attention.",
      },
      {
        id: "co-badges",
        title: "Understanding DBS and First Aid status badges",
        summary: "What the coloured badges mean.",
        content: "- **Grey**: valid certificate (more than 60 days remaining)\n- **Amber**: expiring within 31-60 days, plan renewal\n- **Red**: expiring within 30 days or already expired, urgent action needed",
      },
      {
        id: "co-expiring",
        title: "What the Expiring Certs filter shows",
        summary: "Filter to see only staff with certificate concerns.",
        content: "On the Staff page, clicking **Expiring Certs** filters the list to show only staff whose DBS or First Aid certificates are expired or expiring within 60 days.",
      },
      {
        id: "co-risk-assessments",
        title: "Managing risk assessments",
        summary: "Pre-populated library of required assessments with review reminders.",
        content: "The **Risk Assessments** section on the Compliance page lists every assessment required by EYFS, fire, indoor & outdoor environment, kitchen/food, outings, manual handling, intimate care, and any custom ones you add.\n\nEach record stores the date completed, who completed it, the review frequency (annual or termly), the next review date, and a status badge:\n\n- **Current**: review more than 30 days away\n- **Due Soon**: review within 30 days (amber)\n- **Overdue**: review date has passed (red)\n\nClick a row to upload the signed assessment PDF, log mitigations, and confirm the next review. Overdue assessments reduce your Ofsted Readiness score and appear in the Alerts Centre.",
      },
      {
        id: "co-outdoor-check",
        title: "Daily outdoor space check",
        summary: "Morning safety sweep before children use the outdoor area.",
        content: "Every day the outdoor space must be checked before children use it. A banner appears on the Register page each morning until the check is logged.\n\nIt captures: weather, surfaces clear of hazards, gates and fences secure, equipment condition, no animal fouling, and the name of the staff member completing it. Skipped checks appear in the Alerts Centre.",
      },
      {
        id: "co-fire-safety",
        title: "Fire safety: drills, equipment & evacuation",
        summary: "Termly drills, equipment service logs and evacuation procedure.",
        content: "**Fire drills**: A drill must run at least once per academic term (Autumn, Spring, Summer). Each drill records date, children & staff present, evacuation time, weather and issues observed. A missing termly drill flags as Critical.\n\n**Equipment register**: Track extinguishers, alarms, smoke detectors and emergency lighting with last/next service dates. Overdue items cap Fire Safety at 50%.\n\n**Evacuation procedure**: Upload your written procedure document. Required for full compliance.",
      },
      {
        id: "co-medicines",
        title: "Medicine administration & parental consent",
        summary: "Immutable log of all medication given on-site.",
        content: "Each child profile has a **Medicines** tab where Editors, Admins and Owners record parental consents (one-off or ongoing) and log every dose given.\n\nConsent captures: medicine name, dosage, route, parental signature confirmation, start/end dates. Ongoing consents (e.g. Calpol) can be templated.\n\nEvery administration captures: child, medicine, dose, time, route, the staff member who administered it, and a witness. **Records are immutable**: once saved they cannot be edited or deleted, only annotated with a correction note.\n\nDoses given without a valid consent are flagged Critical on Ofsted Readiness, and the Register shows an Unsigned Medicines banner.\n\n**Who can do this:** Editors and above. **Viewers cannot log medicine given.** Safeguarding leads can see accident, incident, behaviour and complaint records whatever their access level.",
      },
      {
        id: "co-ofsted-readiness",
        title: "Ofsted Readiness score (recalibrated)",
        summary: "How the overall compliance score is calculated.",
        content: "The score combines weighted categories: Safeguarding & DBS, Qualifications, Ratios, Children's Records, Risk Assessments, Fire Safety, Medicines, Attendance, and Policies.\n\n**Critical breaches cap the overall score at 59%** regardless of other category scores. Critical breaches include: a current ratio breach, no Level 3 manager on site, a PFA coverage gap, an unconsented medication, an overdue fire drill in the current term, or an overdue Critical risk assessment.\n\nThe page lists each breach with a **Fix this now** link. Resolving every critical breach immediately lifts the cap.",
      },
    ],
  },

  // ── Audit Log ──
  {
    id: "audit-log",
    emoji: "📜",
    title: "Audit Log",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "au-what",
        title: "What is the Audit Log?",
        summary: "A record of every important action on the platform.",
        content: "The Audit Log records every significant action taken on the platform, creating records, editing profiles, approving changes, and more. It's your traceable history of who did what and when.",
      },
      {
        id: "au-find",
        title: "How to find the Audit Log",
        summary: "Where to access it.",
        content: "Click **Audit Log** in the sidebar under the Admin section.",
      },
      {
        id: "au-search",
        title: "How to search the Audit Log",
        summary: "Filter entries by type, person, or date.",
        content: "Use the search bar and filters at the top of the Audit Log page to find specific entries. You can filter by record type, user name, or date range.",
      },
    ],
  },

  // ── Settings & Admin ──
  {
    id: "settings",
    emoji: "⚙️",
    title: "Settings & Admin",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "se-invite",
        title: "How to invite a new team member",
        summary: "Create an account and share a setup link.",
        content: "1. Go to **Access** in the sidebar.\n2. Click **Invite Someone**.\n3. Enter their name, email address, and choose their access level.\n4. Click **Create & Get Link**.\n5. You'll see a setup link and a temporary password, **copy these and share them directly** with the new team member via WhatsApp, text, or any secure channel.\n\nThe setup link lets them set their own password. If the link doesn't work, they can log in with their email and the temporary password instead.\n\n⚠️ Invitations are not sent by email, you must share the details manually.",
      },
      {
        id: "se-reset-password",
        title: "How to reset a team member's password",
        summary: "Owners: generate a new temporary password.",
        content: "1. Go to **Access** in the sidebar.\n2. Find the team member in the list.\n3. Hover over their row and click the **key icon** (🔑).\n4. Confirm the reset.\n5. You'll see a new temporary password, **copy it and share it** with the team member.\n\nTheir old password stops working immediately. Ask them to log in with the temporary password and change it from their profile.\n\nThis feature is only available to **Owners**.",
      },
      {
        id: "se-roles",
        title: "How to change a team member's access level",
        summary: "Update someone's role on the platform.",
        content: "1. Go to **Access** in the sidebar.\n2. Find the team member.\n3. Click **Change Role**.\n4. Select the new access level and confirm.",
      },
      {
        id: "se-passcode",
        title: "How to manage your platform passcode",
        summary: "Admins & Owners: set, change, or remove your personal passcode.",
        content: "Your platform passcode is a personal **6-digit PIN** that protects sensitive actions including approving changes, recording actual hours, and exporting compliance data. You are prompted to set it up after onboarding.\n\nTo set or change your passcode at any time, go to **My Profile > Security** or click your profile menu in the top right. Your passcode is personal, no other user, including Owners, can see or reset it.\n\n**Passcode lockout:** After 3 wrong passcode entries, passcode confirmation is locked for 15 minutes. The lockout event is recorded in the Audit Log. If you forget your passcode, email support@nursly.co.uk.",
      },
      {
        id: "se-errors",
        title: "How to view System Error Logs",
        summary: "Owners: check for platform issues.",
        content: "Go to **Settings** and look for the **Error Logs** section. This shows any technical errors that have occurred. If you see repeated errors, contact support.",
      },
      {
        id: "se-nursery-config-super",
        title: "Nursery configuration (Owner)",
        summary: "Configure active zones and operating hours.",
        content: "You can configure which zones your nursery operates and your standard operating hours in **Settings > Nursery Configuration**. Changes you make take effect immediately. Admins can propose changes to these settings, but their changes require your approval before taking effect, you will receive a notification to review them on the Approvals page.",
      },
      {
        id: "se-nursery-config-admin",
        title: "Nursery configuration (Admin)",
        summary: "View and propose changes to zone and operating hour settings.",
        content: "**Who can do this:** Admins propose, Owners approve. Editors and Viewers cannot open nursery settings.\n\nYou can view your nursery's zone and operating hour configuration in **Settings > Nursery Configuration**. To propose a change, edit the relevant setting and click **Submit for approval**. Your Owner will review and approve or reject the change. You will be notified of their decision.",
      },
      {
        id: "se-session-signin",
        title: "Session & sign-in behaviour",
        summary: "How your login session works.",
        content: "You will not be signed out unless you choose to sign out. Your session persists for 30 days of inactivity. To sign out, click **Sign out** in the sidebar or profile menu.",
      },
    ],
  },

  // ── Security & Trust ──
  {
    id: "security",
    emoji: "🔒",
    title: "Security & Trust",
    visibleTo: ALL,
    articles: [
      {
        id: "sec-overview",
        title: "How Nursly protects your data",
        summary: "A summary of all security measures in place.",
        content: "Nursly protects your data with AES-256 encryption at rest, HTTPS encryption in transit, and role-based access controls enforced at the database level. Your nursery's data is completely isolated from all other nurseries on the platform. Every action is logged in a tamper-proof audit trail.\n\nNursly's Security Policy is publicly available at **nursly.co.uk/security**. It covers encryption, access controls, authentication, data retention, and incident response. No login is required to read it.",
      },
      {
        id: "sec-data",
        title: "What data does Nursly store?",
        summary: "An overview of the data held on the platform.",
        content: "Nursly stores information about children (names, dates of birth, session times, parent contacts), staff (names, qualifications, DBS records), and attendance records. All data is stored securely and only accessible to authorised team members within your nursery.",
      },
      {
        id: "sec-secure",
        title: "How is data kept secure?",
        summary: "Security measures in place.",
        content: "All data is encrypted in transit (HTTPS/TLS) and at rest (AES-256). Access is controlled by role-based permissions, each team member can only see what their access level allows. Sensitive actions require a personal passcode for additional security. Your nursery's data is completely isolated from other nurseries at the database level.",
      },
      {
        id: "sec-access",
        title: "Who can see what, access levels explained",
        summary: "Understanding the five access levels.",
        content: "- **Viewer**: their own room only: can sign children in and out and tick club pickups and drop-offs, but cannot edit anything\n- **Editor**: their own room: everything a Viewer can do, plus editing child profiles and session details, logging medicine given, and editing the rota, all for their own room. Editors cannot archive a child\n- **Front Desk**: reception and nursery administration, across every room: sign any child in and out, tick club pickups and drop-offs, keep child contact, session, funding and SEND details up to date, add children, turn enrolment requests into children and archive children who have left, run the rota for every room, keep staff DBS and First Aid renewals on track, read risk assessments, and create additional day request links. Cannot import spreadsheets or restore archived children, log medicine, sign off risk assessments, invite staff, or open billing, settings or safeguarding referrals\n- **Admin**: full day-to-day access across every room, including approvals, staff, compliance and reports. No billing\n- **Owner**: everything an Admin can do, plus billing and account control\n\nTwo extra designations sit on top of these levels and change nothing else: the **Designated Safeguarding Lead** (opens safeguarding records only) and the **Designated Successor** (no extra access unless you are ever unavailable).",
      },
      {
        id: "sec-sync",
        title: "How platform access and staff profiles are linked",
        summary: "Understanding the sync between login accounts and staff profiles.",
        content: "Every staff member who needs to use the platform (including the Room Register) must have two things:\n1. A platform login account, created via an invitation sent from Access Management\n2. A staff profile on the Staff page, containing their zone, role, contracted hours, and rota assignments\n\nThese two records are linked automatically when the email address on the staff profile matches the email address of the platform login account. Once linked, the staff member's Room Register access is controlled by their zone assignment and rota shifts.\n\n**Owners and Admins** do not require a linked staff profile, their platform access is unrestricted regardless of zone or shift assignments.",
      },
      {
        id: "sec-passcode",
        title: "What the platform passcode is used for",
        summary: "When and why the passcode is required.",
        content: "Your platform passcode is a personal 6-digit PIN required for sensitive actions like approving changes, recording actual hours, and exporting compliance data. It is set up individually by each Admin and Owner and stored securely (hashed). No other user, including Owners, can see or reset your passcode. Passcode events are recorded in the Audit Log.\n\n**Lockout:** After 3 wrong passcode entries, passcode confirmation is locked for 15 minutes. If you forget your passcode, email support@nursly.co.uk.",
      },
      {
        id: "sec-super-admin-alerts",
        title: "Security alerts for Owners",
        summary: "Owners: how you are notified of security events.",
        content: "As an Owner, you receive alerts for critical security events including role changes and data exports. You can review all security events in the **Audit Log**, filtered by the 🔒 Security Events category. If you suspect a security incident, email support@nursly.co.uk immediately.",
      },
      {
        id: "sec-report",
        title: "How to report a security concern",
        summary: "What to do if something seems wrong.",
        content: "If you notice anything unusual, like someone accessing the platform who shouldn't be, or data that looks wrong, speak to your manager or an Owner immediately. They can investigate and take action. For urgent security concerns, email support@nursly.co.uk.",
      },
    ],
  },

  // ── Out of School ──
  {
    id: "out-of-school",
    emoji: "🎒",
    title: "Out of School",
    visibleTo: ALL,
    articles: [
      {
        id: "oos-overview",
        title: "What is the Out of School zone?",
        summary: "Overview of the Out of School provision.",
        content:
          "Out of School covers all out-of-school activities run by the nursery, including Breakfast Club, After School Club, and Holiday Club. It operates as a separate zone from the core nursery (Baby Zone, Toddler Zone, Pre-School) and serves school-age children aged 4-14.\n\nThe staffing ratio for Out of School is 1:8 (one staff member to eight children). Session types are programme-based: Breakfast Club (07:30-09:00), After School Club (15:30-18:30), and Holiday Club (Full Day, Morning, or Afternoon).",
      },
      {
        id: "oos-register",
        title: "Out of School Room Register",
        summary: "How to use the Room Register for Out of School.",
        content:
          "Out of School staff can access the Out of School Room Register from the dedicated Room Register page in the sidebar. The register shows children enrolled in the programme running that day (Breakfast Club, After School Club, or Holiday Club).\n\nAccess works the same as other zones, you need a linked staff profile with Out of School as your primary zone and an active rota shift.",
      },
      {
        id: "oos-enrol",
        title: "How to enrol a child in Out of School",
        summary: "Adding a child to Out of School provision.",
        content:
          "1. Go to **Children** and click **Add Child**.\n2. In Step 2 (Nursery Details), select **Out of School** as the zone.\n3. Select the **Programme(s)** the child is enrolled in, Breakfast Club, After School Club, or Holiday Club.\n4. Optionally enter the child's **School Name**, **Year Group**, and **School Pickup Contact**.\n5. Complete the remaining steps as normal.\n\nNote: Government funded options (15hrs/30hrs) do not apply to Out of School children. They are privately paid by default.",
      },
      {
        id: "oos-dual",
        title: "Dual enrolment (nursery + Out of School)",
        summary: "How children can attend both nursery and Out of School.",
        content:
          "A child already enrolled in a nursery zone (e.g. Pre-School) can also be enrolled in Out of School. Dual-enrolled children appear in both zones' registers on their relevant days.\n\nTo set up dual enrolment, edit the child's profile and add Out of School as a secondary enrolment. Their profile will show both zone badges.",
      },
      {
        id: "oos-starting-school",
        title: "Starting School transition",
        summary: "Transitioning a child from Pre-School to Out of School.",
        content:
          "When a child is leaving the nursery to start school but will continue attending Out of School provision, use the **Starting School** transition type.\n\nThis is triggered manually by a manager from the Transitions page. On completion, the child's nursery enrolment ends and an Out of School enrolment begins.",
      },
      {
        id: "oos-rota",
        title: "Out of School rota and shift templates",
        summary: "Managing shifts for Out of School staff.",
        content:
          "Out of School has dedicated shift templates:\n- **Breakfast Club Open**: 07:30-09:00\n- **After School Close**: 15:00-18:30\n- **Holiday Club Full**: 07:30-18:30\n- **Holiday Club AM**: 07:30-13:30\n- **Holiday Club PM**: 13:00-18:30\n\nYou can filter the rota by Out of School to see only OoS staff shifts. No Out of School shift may start before 07:30.",
      },
      {
        id: "oos-compliance",
        title: "Out of School staffing compliance",
        summary: "How ratio compliance works for Out of School.",
        content:
          "The Out of School staffing ratio is **1:8**: the same as Pre-School. The Compliance page shows Out of School as its own row in the ratio table.\n\nDays with no Out of School session show '-' on the compliance table, this is not a compliance failure. Level 3 Early Years qualification requirements do not apply to Out of School staff.",
      },
    ],
  },

  // ── Pickups & Drop-offs ──
  {
    id: "pickups",
    emoji: "🚐",
    title: "Pickups & Drop-offs",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "pk-overview",
        title: "What is the Pickups & Drop-offs module?",
        summary: "Overview of the After School and Breakfast Club pickup system.",
        content:
          "The Pickups & Drop-offs module manages the collection and delivery of children for After School Club and Breakfast Club.\n\n- **After School Club**: Staff collect children from school, sign them into the nursery building, and parents collect them later.\n- **Breakfast Club**: Parents sign children in at the nursery, staff transport them to school.\n\nParents book sessions monthly via a public booking form. Today's Pickups shows all children to be collected or dropped off today, organised by school.",
      },
      {
        id: "pk-todays",
        title: "How to use Today's Pickups",
        summary: "Track and manage daily pickups and drop-offs.",
        content:
          "1. Click **Today's Pickups** in the sidebar under Pickups & Drop-offs.\n2. Switch between **Breakfast Club** and **After School Club** tabs.\n3. Each school card shows the children booked for that service today.\n\n**After School Club flow**:\n- Tick the checkbox when you collect a child from school\n- Admin clicks **Sign in to building** when the child arrives at the nursery\n- Admin clicks **Sign out, parent collected** when the parent picks up\n\n**Breakfast Club flow**:\n- Parent signs child in at the nursery\n- Staff ticks **Boarded vehicle** when the child gets on the transport\n- Staff clicks **Confirmed school drop** when the child is dropped at school",
      },
      {
        id: "pk-emergency",
        title: "How to add an emergency child",
        summary: "Add a same-day pickup that wasn't pre-booked.",
        content:
          "On the Today's Pickups page, click **Add emergency child** under any school card. Enter the child's name, class, parent name, and contact number. The child appears on the list immediately.",
      },
      {
        id: "pk-not-collected",
        title: "What to do if a child could not be collected",
        summary: "Record why a child wasn't picked up.",
        content:
          "On the Today's Pickups page, click **Could not collect** next to the child. Select a reason from the dropdown (e.g. child absent from school, parent collected directly). Add notes if needed.",
      },
      {
        id: "pk-booking-form",
        title: "How the parent booking form works",
        summary: "Parents book sessions via a public link.",
        content:
          "Parents access the booking form at a public URL (no login required). They select their child's school, choose a month, and tick the days they need Breakfast Club and/or After School Club.\n\nSubmitted bookings appear on the Booking Management page for admin review. Admins can link bookings to existing child records.",
      },
      {
        id: "pk-schools",
        title: "How to manage schools",
        summary: "Edit school details, pickup times, and assigned staff.",
        content:
          "1. Click **Schools** in the sidebar under Pickups & Drop-offs.\n2. Click the pencil icon on any school card to edit.\n3. Update the name, address, pickup time, drop-off time, default pickup staff, and active status.\n4. Click **Save**.",
      },
      {
        id: "pk-bookings",
        title: "How to review and link bookings",
        summary: "Match parent submissions to child records.",
        content:
          "1. Go to **Booking Management** in the sidebar.\n2. Unlinked bookings show an amber alert.\n3. Click **Link** to match a booking to an existing child record.\n4. Search by name and confirm the link.",
      },
    ],
  },

  // ── What's New ──
  {
    id: "whats-new",
    emoji: "🆕",
    title: "What's New",
    visibleTo: ALL,
    articles: [
      {
        id: "wn-front-desk",
        title: "New Front Desk access level",
        summary: "Reception and nursery administration access, across every room.",
        content: "There is a new access level for the person on the front desk. Front Desk can sign any child in and out, tick club pickups and drop-offs, keep child contact, session, funding and SEND details up to date, add children, turn enrolment requests into children and archive children who have left, run the rota for every room, keep staff DBS and First Aid renewals on track, read risk assessments, and create additional day request links.\n\nTo keep care records with the care team, Front Desk cannot import spreadsheets or restore archived children, log medicine, sign off risk assessments, invite staff, or open billing, settings or safeguarding referrals. Safeguarding Lead and Successor designations still work alongside it.\n\nAn Owner or Admin can set someone to Front Desk from **Access Management**.",
      },
      {
        id: "wn-1",
        title: "Room Register added",
        summary: "March 2026: staff can now sign children in and out at room level.",
        content: "The Room Register is a new feature that lets zone staff sign children in and out directly from their room. This is faster and simpler than using the main nursery-wide register.",
      },
      {
        id: "wn-2",
        title: "Planned Changes auto-application",
        summary: "March 2026: changes now apply automatically on the effective date.",
        content: "Planned Changes now apply automatically on their effective date. You no longer need to manually apply them, the system handles it overnight.",
      },
      {
        id: "wn-3",
        title: "Operations staff role added",
        summary: "March 2026: non-childcare team members now have their own category.",
        content: "Operations staff (receptionists, cleaners, caretakers) are now handled as a separate category. They appear on the Rota without being assigned to a childcare zone.",
      },
      {
        id: "wn-4",
        title: "Platform Access & Staff Profile sync",
        summary: "March 2026: login accounts and staff profiles are now linked automatically.",
        content: "Platform login accounts and staff profiles are now linked automatically when their email addresses match. This controls Room Register access based on zone and shift assignments. Admins can view link status on the Staff page, send invites directly, and run a bulk audit to find unlinked accounts.",
      },
      {
        id: "wn-5",
        title: "Out of School zone launched",
        summary: "March 2026: Breakfast Club, After School Club, and Holiday Club are now fully integrated.",
        content: "The Out of School zone is now live across the entire platform. It covers Breakfast Club, After School Club, and Holiday Club provision for school-age children. Ratio compliance (1:8), dedicated shift templates, register access, and full reporting are all included.",
      },
      {
        id: "wn-6",
        title: "School Holiday Calendar & Pickups Module launched",
        summary: "April 2026: holiday-aware attendance and full pickup/drop-off management.",
        content: "Two major features launched:\n\n**School Holiday Calendar**: Set school holiday periods in Settings. Term-time-only funded children are automatically excluded from attendance counts, registers, and compliance ratios during holidays.\n\n**Pickups & Drop-offs**: A fully integrated module for managing After School Club and Breakfast Club collections. Includes parent booking forms, Today's Pickups dashboard, school management, booking review, and real-time pickup tracking.",
      },
      {
        id: "wn-7",
        title: "Free data migration with data protection",
        summary: "April 2026 - secure migration flow with encryption and access controls.",
        content: "If you are on the Professional plan, free migration is included. We move your existing children records, staff profiles, DBS dates and room assignments into Nursly for you. Start the migration from Settings > Migration or click the banner on your Dashboard. Your data is encrypted in transit and at rest, and your uploaded source files are deleted within 14 days of migration completion. Starter plan users can complete the migration form before upgrading - the payment step appears at submission only.",
      },
      {
        id: "wn-additional-days",
        title: "Additional Days & Extended Hours",
        summary: "August 2026: extra day requests, including a link parents can fill in themselves.",
        content: "Parents asking for an extra day or longer hours now go through one list. Send them a request link, check the date has room, then approve, adjust or decline. Approved days flow straight into the register, headcounts and ratios for that date. See the **Additional Days & Extended Hours** section.",
      },
      {
        id: "wn-safeguarding-log",
        title: "Accident, Incident & Behaviour log",
        summary: "August 2026: one permanent record of accidents, incidents and behaviour concerns.",
        content: "Log accidents, incidents and behaviour concerns as they happen, including what you did and when the parent was told. Saved entries are locked and cannot be edited, add a follow-up note instead. Entries are kept until the child's 21st birthday and are included in the Ofsted pack.",
      },
      {
        id: "wn-complaints-log",
        title: "Complaints log with 28-day tracking",
        summary: "August 2026: record every complaint and show you responded in time.",
        content: "Every complaint is logged with who raised it, what was done, and the outcome. Nursly counts the 28 days you have to respond and reminds you before the deadline. Resolved complaints are locked, and the log is included in the Ofsted audit pack.",
      },
      {
        id: "wn-minimal-profiles",
        title: "Add children and staff with just a name",
        summary: "August 2026: start a profile now, finish it later.",
        content: "You no longer need every field to save a new child or staff member. Add a first and last name and save. An **Incomplete profile** badge lists what is still needed so you can fill it in when the paperwork arrives.",
      },
      {
        id: "wn-allergies-export",
        title: "Allergies and medical conditions list",
        summary: "August 2026: printable list for the kitchen and rooms.",
        content: "From the **Children** page Export menu you can now download an allergies and medical conditions list as a PDF to print, or a CSV for Excel. It shows each child's zone, condition and emergency contact.",
      },
      {
        id: "wn-kiosk-staff",
        title: "Staff sign-in and out on the kiosk",
        summary: "August 2026: real hours on the rota, with auto sign-out flagged.",
        content: "Staff can now sign in and out on the kiosk tablet. Real times are saved against the shift and marked with 📍. If the planned and actual times differ by more than 30 minutes, or someone forgot to sign out and the shift shows **Auto sign-out: verify hours**, a manager confirms the correct time on the Rota page.",
      },
      {
        id: "wn-dsl-successor",
        title: "Safeguarding Lead and Designated Successor",
        summary: "August 2026: name who leads safeguarding, and who could step in.",
        content: "You can now name a **Designated Safeguarding Lead** and deputy, so the person responsible for safeguarding can reach safeguarding records whatever their everyday access level, and nothing else. You can also name a **Designated Successor** and backup, who gets no extra access today but could be verified and given Owner access if you were ever unavailable. Both are set up in **Settings > Succession**, and the person has to accept within 14 days. See the **Safeguarding Lead & Succession** section for the detail.",
      },
      {
        id: "wn-owner-rename",
        title: "'Super Admin' is now called 'Owner'",
        summary: "August 2026: same access, clearer name, plus a corrected Viewer description.",
        content: "The highest access level is now shown as **Owner** everywhere instead of Super Admin. Nothing about what it can do has changed.\n\nWe also corrected our own guidance on the **Viewer** level. Viewers can sign children in and out and tick club pickups and drop-offs for their own room, they were described in places as read-only, which was wrong. Viewers still cannot edit the rota, child profiles or the medicine log. Editors can now edit the rota for their own room.",
      },
      {
        id: "wn-permissions-guide",
        title: "Access and Permissions guide",
        summary: "August 2026: see exactly what each access level can do.",
        content: "**Access Management → Permissions guide** now shows, page by page, what Owners, Admins, Editors and Viewers can each see and do. Check it before you invite someone so you give the lowest access level they need.",
      },
      {
        id: "wn-ofsted-pack-expanded",
        title: "Ofsted audit pack now covers safeguarding and complaints",
        summary: "August 2026: one download, more evidence.",
        content: "The one-click audit pack now also includes accident, incident and behaviour summaries, the complaints log with response times, training and certificate records, and a policy checklist. Confidential LADO and social care records are never included.",
      },
    ],
  },
  // ── Ofsted Readiness ──
  {
    id: "ofsted-readiness",
    emoji: "🛡️",
    title: "Ofsted Readiness",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "ofsted-score",
        title: "What the Ofsted Readiness Score means",
        summary: "Understanding the composite readiness score and its nine categories.",
        content:
          "The Ofsted Readiness Score is a live percentage calculated from nine categories: Safeguarding & DBS, Staff Qualifications, Staffing Ratios, Attendance & Register, Children's Records, Policies & Documentation, Risk Assessments, Fire Safety, and Medicine Administration.\n\nEach category is scored from 0-100% based on real data from the platform. The overall score is the average of all nine. It updates automatically as data changes, it is not a vanity metric but reflects genuine gaps that Ofsted would flag.\n\n- 90%+ = Excellent\n- 75-89% = Good\n- 50-74% = Requires Improvement\n- Below 50% = Inadequate",
      },
      {
        id: "ofsted-report",
        title: "How to view the Ofsted Readiness Report",
        summary: "Navigate to the report and interpret each section.",
        content:
          "Go to **Ofsted Readiness** in the sidebar (under Compliance). The report shows:\n\n1. **Overall Score** at the top with a breakdown by category\n2. **Safeguarding & DBS**: valid, expiring, and expired DBS counts\n3. **Qualifications**: Level 3+ percentage, First Aid status\n4. **Ratios**: per-zone compliance against EYFS requirements\n5. **Attendance**: register completion and low attendance flags\n6. **Children's Records**: completeness of emergency contacts and key fields\n7. **Policies**: review dates for key documents",
      },
      {
        id: "ofsted-export",
        title: "How to export the Ofsted Audit Pack",
        summary: "Step-by-step guide to generating the full audit export.",
        content:
          "1. Go to **Ofsted Readiness** in the sidebar.\n2. Click **Export Audit Pack** in the top right.\n3. Select the term or date range.\n4. Tick the data categories you want to include.\n5. Click **Generate & Download Pack**.\n\nThe platform will generate CSV files for each selected category and download them. Every export is logged in the Audit Log with your name and timestamp.",
      },
      {
        id: "ofsted-policies",
        title: "How to update the Policies & Documentation checklist",
        summary: "Mark policies as reviewed and add notes.",
        content:
          "On the Ofsted Readiness page, scroll to the **Policies & Documentation** section. Each policy shows its last reviewed date.\n\nOwners can click **Update** next to any policy to set a new review date and add notes. Policies not reviewed in over 12 months show an amber warning.\n\nThe six default policies are: Safeguarding, Health & Safety, SEND, Behaviour Management, Complaints, and Staff Handbook.\n\nAll uploaded policy documents are stored permanently. When you upload a new version, the previous version is archived rather than deleted. Both current and all previous versions are included in your Ofsted Audit Pack export.",
      },
      {
        id: "ofsted-retention",
        title: "Data retention, what is kept and for how long",
        summary: "Understanding the platform's permanent data retention policy.",
        content:
          "No data is ever permanently deleted from Nursly. When a record is 'deleted', it is archived, hidden from active views but retained in the database.\n\n**Retention periods** (per UK early years guidance):\n- Child records: until child's 25th birthday (26th if SEND)\n- Staff records: 6 years after leaving\n- DBS records: duration of employment + 6 years\n- Attendance registers: 3 years minimum\n- Safeguarding records: indefinite\n- Rota and hours records: 6 years\n- Pickup/drop-off logs: 3 years minimum\n\nArchived records remain searchable via the 'Include archived' toggle on relevant pages.",
      },
    ],
  },
  // ── Audit Log ──
  {
    id: "audit-log",
    emoji: "📜",
    title: "Audit Log",
    visibleTo: SUPER_ONLY,
    articles: [
      {
        id: "audit-what",
        title: "What the Audit Log records and why it matters",
        summary: "Understanding the platform's comprehensive activity trail.",
        content:
          "Every action taken on the platform is recorded in the Audit Log: record creation, edits, archiving, sign-in/sign-out events, approvals, exports, settings changes, and more.\n\nThe Audit Log is append-only, no one can edit or delete entries, including Owners. This creates a tamper-proof record for regulatory purposes.\n\nAudit log entries are retained for a minimum of 6 years.",
      },
      {
        id: "audit-search",
        title: "How to search and filter the Audit Log",
        summary: "Finding specific activity records quickly.",
        content:
          "Go to **Audit Log** in the sidebar (Admins and Owners only). Use the filters at the top:\n\n- **Search**: find by action description or user name\n- **Status filter**: filter by Approved, Alert, or Pending\n\nYou can search for a specific child or staff member's name to see all actions taken on their record.",
      },
      {
        id: "audit-export",
        title: "How to export the Audit Log",
        summary: "Exporting activity records for regulatory purposes.",
        content:
          "The Audit Log can be exported as part of the Ofsted Audit Pack from the Ofsted Readiness page. Select 'Full Audit Log' in the export options.\n\nEvery export event is itself logged in the Audit Log, creating a clear record of who generated the export and when.",
      },
    ],
  },

  // ── Kiosk Mode ──
  {
    id: "kiosk-mode",
    emoji: "📱",
    title: "Kiosk Mode",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "kiosk-what",
        title: "What is Kiosk mode?",
        summary: "Parent-facing sign-in tablet at the entrance.",
        content:
          "Kiosk mode turns a tablet at your front door into a parent self sign-in screen. Parents tap their child's photo to sign in on arrival or sign out at collection. Sign-ins flow straight into the Room Register in real time, so staff inside see arrivals as they happen.",
      },
      {
        id: "kiosk-setup",
        title: "How to set up Kiosk mode",
        summary: "Generate the URL and load it on a tablet.",
        content:
          "1. Go to **Settings → Kiosk** (Admin or Owner only).\n2. Copy the unique **Kiosk URL** (it includes a secret token).\n3. Open the URL in the tablet's browser at the entrance.\n4. Add the page to the tablet's home screen for a full-screen launcher.\n\nThe URL stays valid until you regenerate it. Keep the link private, anyone with the URL can sign children in for your nursery.",
      },
      {
        id: "kiosk-exit-passcode",
        title: "Exit passcode",
        summary: "Stops parents leaving kiosk mode.",
        content:
          "Set a **4-digit exit passcode** in Settings → Kiosk. Staff enter this passcode to leave kiosk mode on the tablet (for example, to switch back to the admin app at the end of the day). Choose a passcode that staff know but parents do not.",
      },
      {
        id: "kiosk-parent-flow",
        title: "How parents use it",
        summary: "What parents see at the door.",
        content:
          "Parents see a grid of photos for every child currently expected. They tap their child to sign in, confirm the time, and the register updates instantly. Sign-out works the same way at collection, tap the photo, confirm, done. The tablet returns to the photo grid automatically after each action.",
      },
      {
        id: "kiosk-regenerate",
        title: "Regenerating the Kiosk URL",
        summary: "If the link has been shared by mistake.",
        content:
          "If you suspect the URL has leaked, click **Regenerate URL** in Settings → Kiosk. The previous link stops working immediately. Reload the new URL on every tablet that uses kiosk mode.",
      },
      {
        id: "kiosk-staff-signin",
        title: "Staff signing in and out on the kiosk",
        summary: "Staff can clock in and out on the same tablet.",
        content: "As well as children, staff can sign themselves in and out on the kiosk. On the tablet, tap **Staff**, find your name, and confirm.\n\nThe time you tap is the time recorded, nothing is rounded. If you arrive early or stay late, the real times are saved against your shift on the rota.\n\nWhy this matters: it gives you an accurate record of hours worked without anyone having to remember and write them down later.",
      },
      {
        id: "kiosk-staff-today",
        title: "Seeing who is signed in right now",
        summary: "The Staff on site panel.",
        content: "The kiosk shows a **Staff on site today** panel listing everyone currently signed in, with their sign-in time and expected finish. Managers can also see this from the Rota page.\n\nUse it during the day to check who is actually on the floor before moving staff between rooms.",
      },
      {
        id: "kiosk-auto-signout",
        title: "Auto sign-out for forgotten sign-outs",
        summary: "What happens when someone forgets to sign out.",
        content: "If a staff member does not sign out, Nursly closes the shift automatically at the end of the day and marks it **Auto sign-out: verify hours** on the rota.\n\nA manager then checks the real finish time with the staff member and enters it. See **Rota & Shifts → What 'Auto sign-out: verify hours' means** for the steps.\n\nRemind the team that signing out matters as much as signing in.",
      },
    ],
  },

  // ── Ratios & Staffing Compliance ──
  {
    id: "ratios",
    emoji: "📊",
    title: "Ratios & Staffing Compliance",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "ratio-thresholds",
        title: "How age-specific ratios work",
        summary: "Statutory EYFS staff-to-child ratios per zone.",
        content:
          "Nursly calculates ratios per room using the EYFS statutory minimums:\n\n- **Baby Zone (0 to 2 years)**: 1 staff to 3 children\n- **Toddler Zone (2 to 3 years)**: 1 staff to 4 children\n- **Pre-School (3 to 5 years)**: 1 staff to 8 children (1:13 if an Early Years Teacher / EYPS is leading the room)\n- **Out of School (4 to 14 years)**: 1 staff to 8 children\n\nThe Dashboard and Compliance pages show live ratio status per zone. Each child counts in their current zone only.",
      },
      {
        id: "ratio-eyt-toggle",
        title: "EYT / EYPS / QTS enhanced ratio",
        summary: "How the 1:13 Pre-School ratio is enabled.",
        content:
          "Pre-School can operate at **1:13** instead of 1:8 when a degree-level lead is in charge of the room. That means an Early Years Teacher (EYT), an Early Years Professional (EYPS), or a teacher with Qualified Teacher Status (QTS). Tick **EYT / EYPS / QTS qualified lead** on the staff member's profile, then tick **EYT / EYPS / QTS-led session** on their rota shift. The system applies the enhanced ratio automatically while that practitioner is signed in to the Pre-School register. As soon as they sign out, the room reverts to 1:8.",
      },
      {
        id: "ratio-breaks",
        title: "Break exclusions",
        summary: "Why staff on a break do not count towards ratios.",
        content:
          "Staff who are on a recorded break (lunch, comfort break) are excluded from the ratio count for the duration of the break, they are not available to supervise children. Log breaks on the Rota or via the Room Register's break action. Once the break ends, the staff member is included in the ratio again automatically.",
      },
      {
        id: "ratio-breach-alert",
        title: "What triggers a ratio breach alert",
        summary: "How and when you are warned.",
        content:
          "A breach alert fires the moment the live ratio in any room falls below the statutory minimum, for example, if a Baby Zone has 4 children signed in but only 1 staff member available. The alert appears as a red banner on the Dashboard and Register, and is recorded as a Critical breach on Ofsted Readiness until resolved. Resolving the breach (more staff in, child signed out, or break ended) clears the alert immediately.",
      },
      {
        id: "ratio-operations-staff",
        title: "Operations staff",
        summary: "Why they do not count in childcare ratios.",
        content:
          "Staff whose primary zone is **Operations** (cook, cleaner, administrator) are never counted in childcare ratios, even if they are on site. They appear on the rota and Daily Briefing but do not affect ratio calculations for Baby, Toddler, Pre-School, or Out of School.",
      },
    ],
  },

  // ── Additional Days & Extended Hours ──
  {
    id: "additional-days",
    emoji: "➕",
    title: "Additional Days & Extended Hours",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "ad-what",
        title: "What Additional Days requests are for",
        summary: "Handling one-off extra days and longer hours in one place.",
        content: "Parents often ask for an extra day, an earlier drop-off, or a later pick-up. Additional Days keeps every one of those requests in one list instead of on scraps of paper, in text messages, or in someone's memory.\n\nEach request records the child, the dates wanted, the times, who asked, and whether it was approved or turned down. Once approved, the extra day is counted in headcounts, ratios and the register for that date.\n\nWhy this matters: you can see straight away whether saying yes would break your ratios or room limits, and there is a clear record of what was agreed.",
      },
      {
        id: "ad-create",
        title: "How to create a request",
        summary: "Add a request on behalf of a parent.",
        content: "1. Click **Additional Days** in the sidebar.\n2. Click **New request**.\n3. Choose the child.\n4. Pick the date or dates being asked for.\n5. Enter the start and finish time for each date.\n6. Add a note if there is anything the manager should know.\n7. Click **Submit**.\n\nThe request appears in the list as **Pending**. Weekends and closure days cannot be selected.",
      },
      {
        id: "ad-parent-link",
        title: "How the parent link works",
        summary: "Let parents fill the request in themselves.",
        content: "Instead of typing a request out yourself, you can send the parent a link.\n\n1. On the **Additional Days** page, click **Parent request link**.\n2. Copy the link and send it to the parent by email or message.\n3. The parent opens it on their phone, chooses their child, picks the dates and times, and submits.\n\nThe link is unique to your nursery and does not need a login. Parents can only submit a request, they cannot see other children or approve anything. Requests sent this way arrive in your list as **Pending**, exactly like ones you enter yourself.",
      },
      {
        id: "ad-approve",
        title: "How approving and editing works",
        summary: "Approve, change or decline a request.",
        content: "Open a **Pending** request and you will see the dates asked for, plus a check of whether each date has room. Then:\n\n- **Approve**: the extra day is confirmed and added to that date's headcount and register.\n- **Decline**: the parent's request is refused. Add a short reason so the record makes sense later.\n- **Edit**: change the times, or remove one date and keep the others, before approving. Useful when a parent asks for 8am but you can only start at 8:30.\n\nOnly Admins and Owners can approve. Editors can create requests and see the outcome.\n\nApprovals and declines are recorded in the Audit Log with the name of the person who made the decision.",
      },
      {
        id: "ad-synced",
        title: "How an approved day appears once synced",
        summary: "Where to see the extra day after approval.",
        content: "Once approved, the extra day appears everywhere the child would normally appear on that date:\n\n- on the **Register** and **Room Register** for that day, marked as an additional day\n- in the **Dashboard** headcount and ratio figures for that date\n- on the child's profile under their attendance pattern for that date only\n- in attendance reports and exports\n\nIt does not change the child's normal weekly pattern. If a parent wants a permanent change, use **Planned Changes** instead.",
      },
      {
        id: "ad-plan",
        title: "Which plan includes Additional Days",
        summary: "Professional plan feature.",
        content: "Additional Days and Extended Hours is part of the **Professional** plan. On the Starter plan the page shows an upgrade prompt. Nothing you have already recorded is lost if you change plan.",
      },
    ],
  },

  // ── Accident, Incident & Behaviour Log ──
  {
    id: "safeguarding-log",
    emoji: "🩹",
    title: "Accident, Incident & Behaviour Log",
    visibleTo: EDITORS_UP,
    articles: [
      {
        id: "sg-what",
        title: "What the log is for",
        summary: "One permanent record of accidents, incidents and behaviour concerns.",
        content: "This log is where you write down anything that happens to a child that needs to be recorded:\n\n- **Accident**: a bump, fall, graze or injury while in your care\n- **Incident**: something that happened elsewhere, for example an injury a child arrived with\n- **Behaviour**: biting, hitting, or a pattern of behaviour you are monitoring\n\nEvery entry sits in one place with the date, time, who was involved, what happened, what you did about it, and whether the parent was told.\n\nWhy this matters: an inspector will ask to see these records, and they will look for entries that are complete, in order, and written on the day. Keeping them here means nothing goes missing in a paper folder.",
      },
      {
        id: "sg-add",
        title: "How to log an entry",
        summary: "Step by step, on the day it happens.",
        content: "1. Click **Accident & Incident Log** in the sidebar.\n2. Click **New entry**.\n3. Choose the type, Accident, Incident or Behaviour.\n4. Choose the child.\n5. Enter the date and time it happened.\n6. Say where it happened (room, garden, outing).\n7. Describe **what happened** in plain words, what you saw, not what you assume.\n8. Describe **what you did**: first aid given, comfort, who was told.\n9. Record any injury and where on the body.\n10. Add the staff member who dealt with it, and a witness if there was one.\n11. Record whether the parent was informed, how, and when.\n12. Read it back, then click **Save entry**.\n\nWrite it while you still remember it clearly. A short, factual entry written the same day is worth far more than a detailed one written a week later.",
      },
      {
        id: "sg-immutable",
        title: "Why entries cannot be changed once saved",
        summary: "Saved entries are locked for good reason.",
        content: "When you click **Save entry**, the record is locked. It cannot be edited or deleted by anyone, including Owners.\n\nThis is deliberate. A safeguarding record that can be quietly changed afterwards is worth nothing as evidence. Locking it protects the child, and it protects you and your staff if the record is ever questioned.\n\n**If you got something wrong or need to add more:** open the entry and add a **follow-up note**. The original stays as it was, and your note is added underneath with your name and the date. This is how corrections are expected to be handled.\n\nBecause of this, please check the child, the date and the description before you save.",
      },
      {
        id: "sg-parent-informed",
        title: "Recording that the parent was told",
        summary: "Never leave this blank.",
        content: "Every entry asks whether the parent or carer was informed, how (in person, phone, message) and when.\n\nIf the parent has not been told yet, save the entry and add a follow-up note as soon as you have spoken to them. Entries where the parent was never recorded as informed are flagged for your manager, because this is one of the first things an inspector checks.",
      },
      {
        id: "sg-retention",
        title: "How long entries are kept",
        summary: "Until the child's 21st birthday.",
        content: "Accident, incident and behaviour entries are kept until the child's **21st birthday**, as required for records of this kind. They are not removed when a child leaves the nursery. You do not need to do anything, Nursly handles this for you.",
      },
      {
        id: "sg-ofsted",
        title: "How the log appears in the Ofsted pack",
        summary: "Included automatically in the audit export.",
        content: "The one-click Ofsted audit pack includes a summary of every accident, incident and behaviour entry, plus totals by type. You can also choose to include the full written detail of each entry.\n\nConfidential LADO referrals and social care flags are **never** included in the Ofsted pack, those are exported separately by an Owner.",
      },
    ],
  },

  // ── Complaints Log ──
  {
    id: "complaints-log",
    emoji: "📣",
    title: "Complaints Log",
    visibleTo: ADMINS_UP,
    articles: [
      {
        id: "cp-what",
        title: "What the Complaints log is for",
        summary: "Every complaint, and what you did about it, in one place.",
        content: "You must keep a written record of every complaint about how the nursery meets the EYFS requirements, and be able to show what you did about it.\n\nThe Complaints log holds each complaint, who raised it, what it was about, the steps you took, and how it was resolved.\n\nWhy this matters: you have **28 days** to respond to the person who complained. Nursly counts those days for you and warns you before the deadline, so nothing slips. An inspector can ask to see this log at any time.",
      },
      {
        id: "cp-add",
        title: "How to log a complaint",
        summary: "Record it as soon as it is raised.",
        content: "1. Click **Complaints Log** in the sidebar.\n2. Click **New complaint**.\n3. Enter the date it was received.\n4. Say who raised it, parent, staff member, visitor or anonymous.\n5. Choose what it relates to (for example safeguarding, care practice, food, staffing, or something else).\n6. Describe the complaint in the complainant's own terms as far as you can.\n7. Record how it arrived, in person, by phone, by email, or in writing.\n8. Name the person handling it.\n9. Click **Save**.\n\nThe complaint is logged as **Open** and the 28-day clock starts from the date received.",
      },
      {
        id: "cp-progress",
        title: "How to progress a complaint",
        summary: "Adding investigation steps and updates.",
        content: "Open the complaint and add an update each time something happens, a conversation, a meeting, a document reviewed, advice taken.\n\nEach update records the date, what was done, and who did it. Nothing you add is removed later, so the log shows the full story in order.\n\nWhile a complaint is open you will see how many of the 28 days are left. A reminder banner appears on the Compliance page when a complaint is getting close to the deadline or has passed it.",
      },
      {
        id: "cp-resolve",
        title: "How to resolve and close a complaint",
        summary: "Recording the outcome.",
        content: "1. Open the complaint and click **Resolve**.\n2. Enter the date you responded to the complainant.\n3. Record the outcome, upheld, partly upheld, or not upheld.\n4. Write what you decided and what you changed as a result, if anything.\n5. Confirm how the complainant was told.\n6. Click **Save resolution**.\n\nOnce resolved, the complaint is **locked**. The record cannot be edited afterwards, for the same reason as safeguarding entries, it needs to be trustworthy. If something further happens, add a follow-up note.\n\nComplaints resolved within 28 days show as **Responded in time**. Ones that were not are shown honestly as late, which is better than an incomplete record.",
      },
      {
        id: "cp-ofsted",
        title: "Complaints in the Ofsted pack",
        summary: "Included automatically.",
        content: "The Ofsted audit pack includes the complaints log with the response time for each complaint and a summary of how many were answered within 28 days. You do not need to prepare anything separately.",
      },
    ],
  },

  // ── Safeguarding Lead & Succession ──
  {
    id: "leads-and-succession",
    emoji: "🛡️",
    title: "Safeguarding Lead & Succession",
    // Anyone can be nominated, whatever their access level, so everyone can read this.
    visibleTo: ALL,
    articles: [
      {
        id: "ls-two-designations",
        title: "Two designations that sit on top of access levels",
        summary: "What they are, and what they are not.",
        content: "Alongside the four everyday access levels, Viewer, Editor, Admin and Owner, you can name two special designations:\n\n- **Designated Safeguarding Lead** (and a deputy), opens safeguarding records, and nothing else\n- **Designated Successor** (and a backup), no extra access at all today, only if you are ever unavailable\n\nNeither one changes a person's everyday access level. A Viewer who is your safeguarding lead can open safeguarding records, but still cannot edit anything outside their own room, and a Viewer-level lead still cannot edit the rota.",
      },
      {
        id: "ls-dsl-what",
        title: "What the Designated Safeguarding Lead flag is",
        summary: "The person responsible for safeguarding here.",
        content: "Every setting must have a named person who takes lead responsibility for safeguarding, and a deputy who stands in when they are not there. Nursly holds that name and connects it to what they can see.\n\nBeing named as safeguarding lead lets that person see and add:\n\n- accident, incident and behaviour entries\n- complaints\n- LADO referrals\n- social care case flags on children\n\nIt gives them no other rights. They cannot manage billing, change access levels, edit nursery settings, or reach into other rooms.\n\nWhy this matters: your safeguarding lead is often a room leader rather than the owner. Without this, they would either be locked out of the records they are responsible for, or given far more access than they need.",
      },
      {
        id: "ls-dsl-who",
        title: "Who should hold the safeguarding lead flag",
        summary: "One lead, one deputy, both trained.",
        content: "You can name **one** primary lead and **one** deputy at a time.\n\nGive it to the person who has completed designated safeguarding lead training and who parents and staff are told to go to with a concern. That is often the manager, but it does not have to be. The deputy should be someone who is regularly on site when the lead is not.\n\nDo not name someone simply because they are senior, and do not leave the deputy empty, an inspector will ask who covers when your lead is away.",
      },
      {
        id: "ls-dsl-nominate",
        title: "How to nominate a safeguarding lead",
        summary: "An Owner nominates, the person accepts.",
        content: "1. Go to **Settings > Succession**.\n2. Under **Designated Safeguarding Lead**, choose the staff member for the primary lead, and another for the deputy.\n3. Click **Send nomination**.\n\nThe person gets an email and a card on their dashboard asking them to **accept** or **decline**. Nothing changes until they accept, so nobody is quietly handed safeguarding access.\n\nA nomination that is not answered within **14 days** lapses, and you will need to send it again.\n\nAn Owner can change or remove either lead at any time from the same page. Access ends the moment you remove them.",
      },
      {
        id: "ls-successor-what",
        title: "What the Designated Successor is",
        summary: "So your nursery is never locked out of its own records.",
        content: "A Designated Successor is a person you name in advance who could be given Owner access if you are ever unavailable, long-term illness, leaving suddenly, or simply being unreachable.\n\n**They get no extra access today.** Nothing changes for them at all until a succession event, and every step is checked before any access is granted.\n\nWhy this matters: an inspection, a safeguarding request or a payroll deadline does not wait. Without a named successor, a nursery can end up unable to reach its own attendance, staffing and safeguarding records.",
      },
      {
        id: "ls-successor-who",
        title: "Who should be your successor",
        summary: "Someone trusted, and someone as backup.",
        content: "Name a **primary successor** and a **backup successor**. Choose people who would realistically be trusted to run the setting and handle sensitive records, usually your manager or deputy manager, or a co-owner.\n\nA nursery can have up to **three Owners** at once if you would rather share ownership outright, and there must always be at least one. The last remaining Owner cannot be removed, demoted or archived.",
      },
      {
        id: "ls-successor-nominate",
        title: "How to nominate a successor",
        summary: "Same accept or decline flow, 14-day expiry.",
        content: "1. Go to **Settings > Succession**.\n2. Under **Designated Successor**, choose your primary and backup.\n3. Click **Send nomination**.\n\nThey are asked to accept or decline by email and in the platform. Unanswered nominations lapse after **14 days**. You can change or remove a successor whenever you like.",
      },
      {
        id: "ls-handover",
        title: "How a handover actually happens",
        summary: "Planned, or checked by us if you are unreachable.",
        content: "There are three ways ownership can move:\n\n- **Planned handover**: an Owner hands over from **Settings > Succession** whenever they choose.\n- **Inactivity check-in**: if an Owner has not signed in for 60 days, we email to check in. After 90 days, an accepted successor can ask for the account to be transferred.\n- **Emergency request**: a successor or an Admin can ask the Nursly team for help. We verify the request manually before anything changes.\n\nNothing is transferred automatically and nothing is transferred silently. Every change is recorded in the Audit Log.",
      },
    ],
  },

  // ── Confidential Safeguarding ──
  {
    id: "confidential-safeguarding",
    emoji: "🔒",
    title: "Confidential Safeguarding",
    // A safeguarding lead can hold any base access level, so this guidance must
    // be readable by everyone. It explains the rules, it contains no records.
    visibleTo: ALL,
    articles: [
      {
        id: "cs-what",
        title: "What this section holds",
        summary: "LADO referrals and social care involvement, Owners and safeguarding leads.",
        content: "This section holds the most sensitive records you keep:\n\n- **LADO referrals**: where a concern has been raised about an adult working with children\n- **Social care flags**: where a child is known to social care, is a child in need, or is on a protection plan\n\nThis page can only be opened three ways, and no others:\n\n1. by an **Owner**: always\n2. by the accepted **primary or deputy Designated Safeguarding Lead**: always, whatever their everyday access level\n3. by an **Admin using the safeguarding records passcode**: an emergency fallback only, for the rare case where no Owner and no safeguarding lead can be reached. Every use is recorded\n\nNobody else can see or search these records, whatever their job title.",
        },
      {
        id: "cs-add",
        title: "How to record a referral or flag",
        summary: "Keeping the detail out of general records.",
        content: "Go to **Confidential Safeguarding** in the sidebar and choose **New LADO referral** or **New social care flag**.\n\nRecord the dates, who was contacted, the reference number given, and the current status. Keep names of third parties to what is necessary.\n\nOn the child's profile, a discreet private flag is visible only to Owners and your safeguarding leads. Nothing appears to other staff.",
      },
      {
        id: "cs-export",
        title: "The confidential safeguarding summary export",
        summary: "Separate from the Ofsted pack, and behind a password check.",
        content: "You can produce a **Confidential safeguarding summary** PDF from this page. Before it downloads you will be asked to confirm your password again, even though you are already logged in.\n\nThis export is deliberately **not** part of the one-click Ofsted audit pack. Share it only in person, with an inspector or the LADO, and never leave a printed copy lying around. Owners and accepted safeguarding leads can produce it.\n\nWhy this matters: this information can identify children and families in very difficult circumstances. Keeping it separate means a routine document export can never accidentally include it.",
      },
    ],
  },
];


/** Filter sections by role */
export function getVisibleSections(role: AppRole | null): HelpSection[] {
  return helpSections.filter(section => {
    if (section.visibleTo.length === 0) return true;
    if (!role) return false;
    return section.visibleTo.includes(role);
  });
}

/** Search articles across visible sections */
const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "by",
  "do",
  "for",
  "how",
  "i",
  "in",
  "is",
  "it",
  "my",
  "of",
  "on",
  "or",
  "the",
  "to",
  "what",
  "where",
  "with",
]);

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildSearchTokens(value: string): string[] {
  return normalizeSearchText(value)
    .split(" ")
    .filter(token => token.length > 1 && !SEARCH_STOP_WORDS.has(token));
}

export function searchHelpArticles(
  query: string,
  role: AppRole | null
): { section: HelpSection; article: HelpArticle }[] {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  const queryTokens = buildSearchTokens(query);
  const sections = getVisibleSections(role);

  const scoredResults: Array<{
    section: HelpSection;
    article: HelpArticle;
    score: number;
  }> = [];

  for (const section of sections) {
    for (const article of section.articles) {
      const title = normalizeSearchText(article.title);
      const summary = normalizeSearchText(article.summary);
      const content = normalizeSearchText(article.content);
      const combined = `${title} ${summary} ${content}`;

      let score = 0;

      // Highest confidence: exact phrase match
      if (
        title.includes(normalizedQuery) ||
        summary.includes(normalizedQuery) ||
        content.includes(normalizedQuery)
      ) {
        score += 100;
      }

      // Token-based matching for natural questions (e.g. "How do I sign a child in?")
      const matchedTokens = queryTokens.filter(token => combined.includes(token));
      score += matchedTokens.length * 10;

      // Boost title/summary relevance
      const titleTokenHits = queryTokens.filter(token => title.includes(token)).length;
      const summaryTokenHits = queryTokens.filter(token => summary.includes(token)).length;
      score += titleTokenHits * 8 + summaryTokenHits * 4;

      // Include if we have meaningful evidence
      const hasMeaningfulTokenMatch =
        queryTokens.length === 0
          ? score >= 100
          : matchedTokens.length >= Math.min(2, queryTokens.length);

      if (score > 0 && (score >= 100 || hasMeaningfulTokenMatch)) {
        scoredResults.push({ section, article, score });
      }
    }
  }

  return scoredResults
    .sort((a, b) => b.score - a.score)
    .map(({ section, article }) => ({ section, article }));
}
